import type { AxiosResponse } from 'axios';
import { DependencyManager } from '@base-shared/utils/dependency-manager';
import type { paths as GroupPaths, components as GroupComponents } from '@base-shared/types/group.openapi';
import type { components as PlayerComponents, operations as PlayerOperations } from '@base-shared/types/player.openapi';
import type { components as OAuthComponents, operations as OAuthOperations } from '@base-shared/types/oauth.openapi';
import type { components as SubscriptionComponents } from '@base-shared/types/subscriptions.openapi';
import type { components as MediaComponents, operations as MediaOperations } from '@base-shared/types/media.openapi';

const axios = () => DependencyManager.api.getAxios();
const connectionsAxios = () => DependencyManager.api.getConnectionsAxios();

type GroupSchemas = GroupComponents['schemas'];
type GroupParams = GroupComponents['parameters'];
type IdentityProviderDetails = GroupSchemas['IdentityProviderDetails'];
type GroupUserGetOperation = GroupPaths['/group/users/{userid}']['get'];
type GroupUserItem = GroupUserGetOperation['responses'][200]['content']['application/json'];
type PatchUserMeInput = GroupSchemas['PatchUserMeInput'];
type ModifyUserProductsInput = GroupSchemas['ModifyUserProductsInput'];
type ModifyUserProductsOutput = GroupSchemas['ModifyUserProductsOutput'];
type ModifyAccountProductsInput = GroupSchemas['ModifyAccountProductsInput'];
type ModifyAccountProductsOutput = GroupSchemas['ModifiyAccountProductsOutput'];
type groupUserListProductFilter = GroupParams['groupUserListProductFilter'];
type GroupUserListOutput = GroupSchemas['GroupUserListOutput'];
type ListAccountsResponse = GroupSchemas['ListAccountsResponse'];
type GetGroupInfoResponse = GroupSchemas['GetGroupInfoResponse'];
type UpdateGroupInfoRequest = GroupSchemas['UpdateGroupInfoRequest'];
type SwitchAccountResponse = GroupSchemas['SwitchAccountResponse'];
type CreateOrInviteUserInput = GroupSchemas['CreateOrInviteUserInput'];
type ModifyUserRightsInput = GroupSchemas['ModifyUserRightsInput'];
type ModifyUserRightsOutput = GroupSchemas['ModifyUserRightsOutput'];
type CreateAccountRequest = GroupSchemas['CreateAccountRequest'];
type CreateAccountResponse = GroupSchemas['CreateAccountResponse'];
type ExtendPlanExpiryResponse = GroupSchemas['ExtendPlanExpiryResponse'];

export const useUserApi = () => {
  const getIdpInfo = async (userEmail: string): Promise<AxiosResponse<IdentityProviderDetails>> => {
    return await axios().get(`/user/${encodeURIComponent(userEmail)}/idp`);
  };

  const getUser = async (): Promise<AxiosResponse<GroupUserItem>> => {
    return await axios().get('/group/users/me');
  };

  const updateUser = async (payload: PatchUserMeInput) => {
    return await axios().patch('/user/me', payload);
  };

  const deleteUser = async () => {
    return await axios().delete('/user/me');
  };

  const patchModifyUserProducts = async (
    userId: string | undefined,
    products: ModifyUserProductsInput
  ): Promise<AxiosResponse<ModifyUserProductsOutput>> => {
    return await axios().patch(`/group/users/${userId}/products`, products);
  };

  return { getIdpInfo, getUser, patchModifyUserProducts, updateUser, deleteUser };
};

export const useGroupApi = () => {
  const patchModifyAccountProducts = async (products: ModifyAccountProductsInput): Promise<AxiosResponse<ModifyAccountProductsOutput>> => {
    return await axios().patch('/group/products', products);
  };

  const acceptToS = async () => {
    return await axios().patch('/group/me', {
      tos_accept_date: 'now'
    });
  };

  const getGroupUsers = async (product: groupUserListProductFilter = 'all'): Promise<AxiosResponse<GroupUserListOutput>> => {
    const params = { product: product };

    return await axios().get(`/group/users`, { params });
  };

  const getAccounts = async (): Promise<AxiosResponse<ListAccountsResponse>> => {
    return await axios().get('/group/accounts');
  };

  const getGroupInfo = async (): Promise<AxiosResponse<GetGroupInfoResponse>> => {
    return await axios().get('/group/info');
  };

  const putGroupInfo = async (request: UpdateGroupInfoRequest) => {
    return await axios().put('/group/info', request);
  };

  const switchAccount = async (groupId: string): Promise<AxiosResponse<SwitchAccountResponse>> => {
    return await axios().put(`/group/accounts/${groupId}`);
  };

  const postCreateOrInviteUser = async (user: CreateOrInviteUserInput) => {
    return await axios().post('/group/users', user);
  };

  const patchModifyGroupUserRights = async (
    userId: string,
    userRights: ModifyUserRightsInput
  ): Promise<AxiosResponse<ModifyUserRightsOutput>> => {
    return await axios().patch(`/group/users/${userId}/rights`, userRights);
  };

  const deleteGroupUser = async (userId: string) => {
    return await axios().delete(`/group/users/${userId}`);
  };

  const deleteGroup = async () => {
    return await axios().delete('/group/me');
  };

  const postGroupApiKey = async () => {
    return await axios().post('/group/api-key');
  };

  const postCreateAccount = async (
    account: CreateAccountRequest
  ): Promise<AxiosResponse<CreateAccountResponse>> => {
    return await axios().post('/group/accounts', account);
  };

  const postExtendPlanExpiry = async (): Promise<AxiosResponse<ExtendPlanExpiryResponse>> => {
    return await axios().post('/group/plan/expiry');
  };

  return {
    patchModifyAccountProducts,
    acceptToS,
    getGroupUsers,
    getAccounts,
    getGroupInfo,
    putGroupInfo,
    switchAccount,
    postCreateOrInviteUser,
    patchModifyGroupUserRights,
    deleteGroupUser,
    deleteGroup,
    postGroupApiKey,
    postCreateAccount,
    postExtendPlanExpiry
  };
};

type PlayerSchemas = PlayerComponents['schemas'];
type PlayerParams = PlayerComponents['parameters'];
type PlayerGetResponse = PlayerOperations['playerGet']['responses'][200]['content']['application/json'];
type PlayerListGetQuery = PlayerOperations['listPlayers']['parameters']['query'];
type ListPlayerOutput = PlayerSchemas['ListPlayerOutput'];
type AssignPlayerRequest = PlayerSchemas['AssignPlayerRequest'];
type AssignPlayerResponse = PlayerSchemas['AssignPlayerResponse'];
type PlayerInfo = PlayerSchemas['PlayerInfo'];
type PlayerUpdateRequest = PlayerSchemas['PlayerUpdateRequest'];

export const usePlayerApi = () => {
  const getPlayer = async (
    serial: PlayerParams['serial'],
    addIntent?: PlayerParams['addIntent']
  ): Promise<AxiosResponse<PlayerGetResponse>> => {
    const params = addIntent ? { addIntent } : null;

    return await axios().get(`/player/${serial}`, { params });
  };

  const getListPlayer = async (query: PlayerListGetQuery): Promise<AxiosResponse<ListPlayerOutput>> => {
    return await axios().get('/player', { params: query ?? null });
  };

  const postPlayerAssign = async (data: AssignPlayerRequest): Promise<AxiosResponse<AssignPlayerResponse>> => {
    return await axios().post(`/player`, data);
  };

  const deletePlayerRemove = async (serialNumber: PlayerParams['serial']): Promise<AxiosResponse<PlayerInfo>> => {
    return await axios().delete(`/player/${serialNumber}`);
  };

  const putPlayerUpdate = async (
    serialNumber: PlayerParams['serial'],
    data: PlayerUpdateRequest
  ): Promise<AxiosResponse<PlayerInfo>> => (axios().put(`/player/${serialNumber}`, data));

  return {
    getPlayer,
    getListPlayer,
    postPlayerAssign,
    deletePlayerRemove,
    putPlayerUpdate
  };
};

export const useSSOApi = () => {
  const getProvider = async (providerName: string) => {
    return await axios().get(`/sso/${providerName}`);
  };

  const postProvider = async (providerName: string) => {
    return await axios().post(`/sso/${providerName}`);
  };

  return { getProvider, postProvider };
};

type OAuthSchemas = OAuthComponents['schemas'];
type OAuthParams = OAuthComponents['parameters'];
type ListMyAppsResponse = OAuthSchemas['ListMyAppsResponse'];
type ListConnectionsQuery = OAuthOperations['listConnections']['parameters']['query'];
type ListConnectionsResponse = OAuthSchemas['ListConnectionsResponse'];
type CreateConnectionRequest = OAuthSchemas['CreateConnectionRequest'];
type CreateConnectionResponse = OAuthSchemas['CreateConnectionResponse'];
type RenewConnectionRequest = OAuthSchemas['RenewConnectionRequest'];

export const useOAuthApi = () => {
  const getListAvailableApps = async (groupId: OAuthParams['groupId']): Promise<AxiosResponse<ListMyAppsResponse>> => {
    return await connectionsAxios().get(`/myapps/${groupId}`);
  };

  const getListConnections = async (
    groupId: OAuthParams['groupId'],
    query?: ListConnectionsQuery
  ): Promise<AxiosResponse<ListConnectionsResponse>> => {
    const params = query ?? null;

    return await connectionsAxios().get(`/connections/${groupId}`, { params });
  };

  const postCreateConnection = async (
    groupId: OAuthParams['groupId'],
    data: CreateConnectionRequest
  ): Promise<AxiosResponse<CreateConnectionResponse>> => {
    return await connectionsAxios().post(`/connections/${groupId}`, data);
  };

  const postRenewConnection = async (
    groupId: OAuthParams['groupId'],
    connectionId: OAuthParams['connectionId'],
    data: RenewConnectionRequest
  ): Promise<AxiosResponse<CreateConnectionResponse>> => {
    return await connectionsAxios().post(`/connections/${groupId}/${connectionId}`, data);
  };

  const deleteConnection = async (groupId: OAuthParams['groupId'], connectionId: OAuthParams['connectionId']) => {
    return await connectionsAxios().delete(`/connections/${groupId}/${connectionId}`);
  };

  return {
    getListConnections,
    getListAvailableApps,
    postCreateConnection,
    postRenewConnection,
    deleteConnection
  };
};

type SubscriptionSchemas = SubscriptionComponents['schemas'];
type SubscriptionParams = SubscriptionComponents['parameters'];
type ListSubscriptionProductsResponse = SubscriptionSchemas['ListSubscriptionProductsResponse'];
type ListSubscriptionRequestsResponse = SubscriptionSchemas['ListSubscriptionRequestsResponse'];
type GetSubscriptionRequestResponse = SubscriptionSchemas['GetSubscriptionRequestResponse'];
type SubscriptionRequest = SubscriptionSchemas['SubscriptionRequest'];

export const useSubscriptionsApi = () => {
  const getListProducts = async (): Promise<AxiosResponse<ListSubscriptionProductsResponse>> => {
    return axios().get<ListSubscriptionProductsResponse>('/subscriptions/products');
  };

  const getListRequests = (): Promise<AxiosResponse<ListSubscriptionRequestsResponse>> => {
    return axios().get<ListSubscriptionRequestsResponse>('/subscription-requests');
  };

  const getRequest = (
    requestId: SubscriptionParams['subscriptionRequestId']
  ): Promise<AxiosResponse<GetSubscriptionRequestResponse>> => {
    return axios().get<GetSubscriptionRequestResponse>(`/subscription-requests/${requestId}`);
  };

  const createRequest = (
    body: SubscriptionSchemas['CreateSubscriptionRequestBody']
  ): Promise<AxiosResponse<SubscriptionRequest>> => {
    return axios().post<SubscriptionRequest>('/subscription-requests', body);
  };

  const deleteRequest = (
    requestId: SubscriptionParams['subscriptionRequestId']
  ): Promise<AxiosResponse> => {
    return axios().delete(`/subscription-requests/${requestId}`);
  };

  return { getListProducts, getListRequests, getRequest, createRequest, deleteRequest };
};

type MediaSchema = MediaComponents['schemas'];
type ListMediaQuery = MediaOperations['listMedia']['parameters']['query'];
type ListMediaResponse = MediaSchema['ListMediaResponse'];

export const useMediaApi = () => {
  const getMediaList = (
    query: ListMediaQuery,
    abortSignal?: AbortSignal
  ): Promise<AxiosResponse<ListMediaResponse>> => {
    return axios().get<ListMediaResponse>('/media', { params: query, signal: abortSignal });
  };

  return { getMediaList };
};
